import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2 } from 'lucide-react';
import { ExpensesTableProps } from '../types';

export function ExpensesTable({
  isDark,
  expenses,
  isLoading,
  onEdit,
  onDelete,
  formatMoney,
}: ExpensesTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#C9A227]" />
      </div>
    );
  }

  const th = cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500');
  const rowCls = cn(
    'transition-colors',
    isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50',
  );

  return (
    <Table>
      <TableHeader>
        <TableRow className={rowCls}>
          <TableHead className={th}>Дата</TableHead>
          <TableHead className={th}>Заголовок</TableHead>
          <TableHead className={th}>Описание</TableHead>
          <TableHead className={th}>Плательщик</TableHead>
          <TableHead className={th}>Сумма</TableHead>
          <TableHead className={th}>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {expenses.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="text-center py-12 text-slate-500">
              Расходов пока нет
            </TableCell>
          </TableRow>
        ) : (
          expenses.map((expense) => (
            <TableRow key={expense.id} className={rowCls}>
              <TableCell className={cn(isDark ? 'text-slate-300' : 'text-slate-700')}>
                {expense.date}
              </TableCell>
              <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#111111]')}>
                {expense.title}
              </TableCell>
              <TableCell className={cn('max-w-xs truncate', isDark ? 'text-slate-400' : 'text-slate-500')}>
                {expense.description || '—'}
              </TableCell>
              <TableCell>
                <Badge className="rounded-lg font-medium border-0 bg-[#C9A227]/10 text-[#C9A227]">
                  {expense.payer?.name || '—'}
                </Badge>
              </TableCell>
              <TableCell className={cn('font-semibold', isDark ? 'text-white' : 'text-[#111111]')}>
                {formatMoney(expense.amount)}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Редактировать расход"
                    onClick={() => onEdit(expense)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100',
                    )}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Удалить расход"
                    onClick={() => onDelete(expense.id)}
                    className={cn(
                      'h-8 px-2 rounded-lg',
                      isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400' : 'text-slate-600 hover:bg-slate-100 hover:text-red-600',
                    )}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
